import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  selectComparisonModalVisibility,
  selectSelectedOrder,
  setComparisonModalVisibility,
  selectAnnotatedCartItems,
} from "@/entities/annotation";
import { OrderLine } from "@/shared/api/lod-api";
import { usePredictionCart } from "@/features/annotation/hooks/usePredictionCart";
import { ErrorMessage } from "./error-message";

import { Cross1Icon } from "@radix-ui/react-icons";
import {
  AlertDialog,
  Flex,
  IconButton,
  ScrollArea,
  Text,
  Box,
} from "@radix-ui/themes";
import CartIsEmpty from "./cart-is-empty";
import { CartItem } from "@/features/annotation/lib/types";
import {
  calculateQuantityDifference,
  createAnnotatedCartFromTransactions,
} from "@/features/annotation/lib/helpers";
import { useMemo, useCallback } from "react";
import { ComparisonRow } from "./comparison-row";
import { LastFetchedInfo } from "./last-fetched-info";
import { PredictionInfoTooltip } from "@/components/prediction-info-tooltip";
import { CartColumn } from "./cart-column";
import { useSystemTime } from "../hooks/useSystemTime";
import { RefetchButton } from "./refetch-button";

const CompareCarts = () => {
  const isOpen = useAppSelector(selectComparisonModalVisibility);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const annotatedCartItems = useAppSelector(selectAnnotatedCartItems);
  const dispatch = useAppDispatch();

  const {
    data: predictionCartData,
    isLoading: isPredictionLoading,
    isFetching: isPredictionFetching,
    error: predictionError,
    refetch: refetchPrediction,
    timeElapsed: predictionTimeElapsed,
    formattedTimeElapsed: formattedPredictionTimeElapsed,
    journeyAnalysis,
  } = usePredictionCart();

  const { refetch: refetchSystemTime } = useSystemTime();

  const refetchPredictionCartAndSystemTime = useCallback(() => {
    refetchPrediction();
    refetchSystemTime();
  }, [refetchPrediction, refetchSystemTime]);

  const handleClose = useCallback(() => {
    dispatch(setComparisonModalVisibility(false));
  }, [dispatch]);

  const predicate = useCallback((uniqueIdentifier: string) => {
    return {
      orderProduct: (product: OrderLine) =>
        product.sku_base_id?.toString() === uniqueIdentifier ||
        product.barcode === uniqueIdentifier,
      annotatedCartItem: (item: CartItem) =>
        item.skuId?.toString() === uniqueIdentifier ||
        item.barcode === uniqueIdentifier,
      predictionCartItem: (item: OrderLine) =>
        item.sku_base_id?.toString() === uniqueIdentifier ||
        item.barcode === uniqueIdentifier,
    };
  }, []);

  const comparisonData = useMemo(() => {
    const orderProducts = selectedOrder?.lines || [];
    const cartItems = createAnnotatedCartFromTransactions(annotatedCartItems);
    const predictionItems = predictionCartData?.result?.lines || [];

    if (!orderProducts.length && !cartItems.length && !predictionItems.length) {
      return [];
    }

    const uniqueLines = new Set<string>();

    const addLine = (line: OrderLine | CartItem) => {
      const identifier =
        "sku_base_id" in line
          ? (line.sku_base_id?.toString() ?? line.barcode)
          : (line.skuId?.toString() ?? line.barcode);
      uniqueLines.add(identifier);
    };

    const mapLineToRow = (uniqueIdentifier: string) => {
      const orderProduct = orderProducts.find(
        predicate(uniqueIdentifier).orderProduct
      );
      const annotatedCartItem = cartItems.find(
        predicate(uniqueIdentifier).annotatedCartItem
      );
      const predictionCartItem = predictionItems.find(
        predicate(uniqueIdentifier).predictionCartItem
      );

      return { orderProduct, annotatedCartItem, predictionCartItem };
    };

    orderProducts.forEach(addLine);
    cartItems.forEach(addLine);
    predictionItems.forEach(addLine);

    const items = Array.from(uniqueLines.values()).map(mapLineToRow);

    return items.sort((a, b) => {
      const nameA =
        a.orderProduct?.name ||
        a.annotatedCartItem?.name ||
        a.predictionCartItem?.name ||
        "";
      const nameB =
        b.orderProduct?.name ||
        b.annotatedCartItem?.name ||
        b.predictionCartItem?.name ||
        "";
      return nameA.localeCompare(nameB);
    });
  }, [selectedOrder, annotatedCartItems, predictionCartData, predicate]);

  const renderComparisonRows = useCallback(() => {
    const orderProducts = selectedOrder?.lines || [];
    const cartItems = createAnnotatedCartFromTransactions(annotatedCartItems);
    const predictionItems = predictionCartData?.result?.lines || [];

    const isAnnotatedCartEmpty = cartItems.length === 0;
    const isOrderCartEmpty = orderProducts.length === 0;
    const isPredictionCartEmpty = predictionItems.length === 0;

    if (!comparisonData.length) {
      return <CartIsEmpty />;
    }

    if (predictionError) {
      return (
        <div className="relative">
          {comparisonData.map((item, index) => {
            const isQuantityDifferent = calculateQuantityDifference([
              item.annotatedCartItem?.quantity,
              item.orderProduct?.quantity,
            ]);

            return (
              <div key={index} className="flex border-b">
                <div className="w-1/3 min-w-1/3 border-r align-top py-2 px-0">
                  <CartColumn
                    item={item.annotatedCartItem}
                    isCartEmpty={isAnnotatedCartEmpty}
                    isFirstRow={index === 0}
                    isQuantityDifferent={isQuantityDifferent}
                    type="annotated"
                  />
                </div>
                <div className="w-1/3 min-w-1/3 border-r align-top py-2 px-0">
                  <CartColumn
                    item={item.orderProduct}
                    isCartEmpty={isOrderCartEmpty}
                    isFirstRow={index === 0}
                    isQuantityDifferent={isQuantityDifferent}
                    type="order"
                  />
                </div>
                <div className="w-1/3 min-w-1/3 py-2 align-top px-0"></div>
              </div>
            );
          })}
          <div className="absolute top-0 right-0 w-1/3 h-full flex items-center justify-center p-6 bg-white border-l border-gray-200">
            <ErrorMessage error={predictionError} />
          </div>
        </div>
      );
    }

    return comparisonData.map((item, index) => {
      const isQuantityDifferent = calculateQuantityDifference([
        item.annotatedCartItem?.quantity,
        item.orderProduct?.quantity,
        item.predictionCartItem?.quantity,
      ]);

      return (
        <ComparisonRow
          key={index}
          annotatedCartItem={item.annotatedCartItem}
          orderProduct={item.orderProduct}
          predictionCartItem={item.predictionCartItem}
          isPredictionLoading={isPredictionLoading}
          isQuantityDifferent={isQuantityDifferent}
          predictionError={null}
          isAnnotatedCartEmpty={isAnnotatedCartEmpty}
          isOrderCartEmpty={isOrderCartEmpty}
          isPredictionCartEmpty={isPredictionCartEmpty}
          isFirstRow={index === 0}
        />
      );
    });
  }, [
    comparisonData,
    isPredictionLoading,
    predictionError,
    selectedOrder,
    annotatedCartItems,
    predictionCartData,
  ]);

  return (
    <AlertDialog.Root open={isOpen}>
      <AlertDialog.Content
        maxWidth="1200px"
        maxHeight="516px"
        className="p-0 overflow-hidden"
      >
        <Flex
          justify="between"
          align="center"
          className="px-4 py-2 border-b border-gray-200"
        >
          <Text size="2" weight="bold">
            Compare carts
          </Text>
          <IconButton variant="ghost" color="gray" onClick={handleClose}>
            <Cross1Icon />
          </IconButton>
        </Flex>
        <Flex className="h-[439px]" direction="column">
          <Flex className="border-b border-gray-200">
            <Flex
              direction="column"
              flexBasis="33.33%"
              className="border-r border-gray-200 pl-3"
            >
              <Text size="1" weight="bold" className="py-2">
                Annotated cart
              </Text>
            </Flex>
            <Flex
              direction="column"
              flexBasis="33.33%"
              className="border-r border-gray-200 pl-3"
            >
              <Text size="1" weight="bold" className="py-2">
                Order cart
              </Text>
            </Flex>
            <Flex direction="column" flexBasis="33.33%" className="px-3">
              <Box className="py-2">
                <Flex gap="1" justify="between">
                  <Flex align="center" gap="1">
                    <Text size="1" weight="bold" mr="1">
                      Predicted cart
                    </Text>
                    <PredictionInfoTooltip />
                  </Flex>
                  <Flex gap="2">
                    <LastFetchedInfo
                      isStale={predictionTimeElapsed > 30_000}
                      timeElapsed={formattedPredictionTimeElapsed}
                      hasNoJourneys={journeyAnalysis.hasNoJourneys}
                    />
                    <RefetchButton
                      timeElapsed={formattedPredictionTimeElapsed}
                      isPredictionFetching={isPredictionFetching}
                      onRefetch={refetchPredictionCartAndSystemTime}
                    />
                  </Flex>
                </Flex>
              </Box>
            </Flex>
          </Flex>
          <ScrollArea className="h-[calc(100%-41px)]">
            <div className="w-full">{renderComparisonRows()}</div>
          </ScrollArea>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};

export default CompareCarts;
